import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import "../css/NucleotideComponent.css";
import { API_BASE_URL } from "../../config"; 
import { FaArrowLeft } from 'react-icons/fa';

const ITEMS_PER_PAGE = 10;

const NucleotideComponent = () => {
  const { accession: routeAccession } = useParams();
  const navigate = useNavigate();

  const [nucleotides, setNucleotides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [expandedFasta, setExpandedFasta] = useState(null);
  const [expandedGenBank, setExpandedGenBank] = useState(null);
  const [genBankData, setGenBankData] = useState({});
  const [loadingGenBank, setLoadingGenBank] = useState(false);
  const [copied, setCopied] = useState(false);
  const [molTypeFilter, setMolTypeFilter] = useState("all");
  const [activeGenBankView, setActiveGenBankView] = useState(null);

  // ASN1 states
  const [expandedASN1, setExpandedASN1] = useState(null);
  const [asn1Data, setAsn1Data] = useState({});
  const [loadingASN1, setLoadingASN1] = useState(false);
  const [activeASN1View, setActiveASN1View] = useState(null);

  const API_URL = "http://197.156.76.122/api/nucleotide/";

  const fetchNucleotides = useCallback(async () => {
    const params = { page, search: searchQuery };
    if (molTypeFilter !== "all") params.mol_type = molTypeFilter;

    setLoading(true);
    setError(null);

    try {
      const { data } = await axios.get(API_URL, { params });
      if (data.results) {
        setNucleotides(data.results);
        setTotalItems(data.count);
        setTotalPages(Math.ceil(data.count / ITEMS_PER_PAGE));

        // Preload GenBank + ASN1 data
        const genBankMap = {};
        const asn1Map = {};
        data.results.forEach((nuc) => {
          if (nuc.genbank) genBankMap[nuc.accession] = nuc.genbank;
          if (nuc.asn1) asn1Map[nuc.accession] = nuc.asn1;
        });

        setGenBankData(genBankMap);
        setAsn1Data(asn1Map);

      } else {
        setError("Invalid response from API.");
        setNucleotides([]);
      }
    } catch (err) {
      console.error(err);
      setError("Failed to fetch nucleotide data.");
      setNucleotides([]);
    } finally {
      setLoading(false);
    }
  }, [page, searchQuery, molTypeFilter]);

  useEffect(() => {
    fetchNucleotides();
  }, [fetchNucleotides]);

  // Open full GenBank view if route has accession
  useEffect(() => {
    if (routeAccession) openFullGenBankView(routeAccession, false);
  }, [routeAccession]);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setPage(1);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") fetchNucleotides();
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) setPage(newPage);
  };

  const toggleFasta = async (id) => {
    if (expandedFasta?.id === id) return setExpandedFasta(null);

    try {
      const res = await fetch(`${API_URL}${id}/fasta/`);
      if (!res.ok) throw new Error("FASTA not found.");
      const text = await res.text();
      setExpandedFasta({ id, fasta: text.replace(/(.{60})/g, "$1\n") });
      setCopied(false);
    } catch (err) {
      alert("Error viewing FASTA: " + err.message);
    }
  };

  const handleCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      alert("Copy failed: " + err.message);
    }
  };

  const fetchGenBank = async (accession) => {
    setLoadingGenBank(true);
    try {
      const localGenBank = nucleotides.find((nuc) => nuc.accession === accession)?.genbank;
      setGenBankData((prev) => ({
        ...prev,
        [accession]: localGenBank || "❌ GenBank data unavailable."
      }));
    } catch (err) {
      console.error("Error loading local GenBank:", err);
      setGenBankData((prev) => ({
        ...prev,
        [accession]: "❌ Failed to load GenBank data."
      }));
    } finally {
      setLoadingGenBank(false);
    }
  };

  const fetchASN1 = async (accession) => {
    setLoadingASN1(true);
    try {
      const localASN1 = nucleotides.find(
        (nuc) => nuc.accession === accession
      )?.asn1;

      setAsn1Data((prev) => ({
        ...prev,
        [accession]: localASN1 || "❌ ASN1 data unavailable."
      }));
    } catch (err) {
      console.error("Error loading local ASN1:", err);
      setAsn1Data((prev) => ({
        ...prev,
        [accession]: "❌ Failed to load ASN1 data."
      }));
    } finally {
      setLoadingASN1(false);
    }
  };

  const openFullGenBankView = async (accession, updateRoute = true) => {
    setActiveGenBankView(accession);
    if (!genBankData[accession]) await fetchGenBank(accession);
    if (updateRoute) navigate(`/nucleotide/${accession}`, { replace: false });
  };

  const closeFullGenBankView = () => {
    setActiveGenBankView(null);
    navigate(`/nucleotide`, { replace: false });
  };

  const openFullASN1View = async (accession) => {
    setActiveASN1View(accession);
    if (!asn1Data[accession]) await fetchASN1(accession);
  };

  const closeFullASN1View = () => {
    setActiveASN1View(null);
  };

  const handleMolTypeChange = (e) => {
    setMolTypeFilter(e.target.value);
    setPage(1);
  };

  const Pagination = () => (
    <div className="pagination-controls">
         {/* 🔙 Back to Home (ADD THIS HERE) */}
	    <button 
	      className="back-button"
	      onClick={() => navigate('/')}
	    >
	      <FaArrowLeft /> Back to Home
	    </button>
      <button disabled={page === 1} onClick={() => handlePageChange(1)}>⏮</button>
      <button disabled={page === 1} onClick={() => handlePageChange(page - 1)}>◀</button>
      <span> Page {page} / {totalPages} </span>
      <button disabled={page === totalPages} onClick={() => handlePageChange(page + 1)}>▶</button>
      <button disabled={page === totalPages} onClick={() => handlePageChange(totalPages)}>⏭</button>
    </div>
  );

  if (activeGenBankView) {
    const accession = activeGenBankView;
    return (
      <div className="genbank-fullview">
        <div className="genbank-header">
          <button className="back-button" onClick={closeFullGenBankView}>← Back to List</button>
          <h2>🧬 GenBank Record: {accession}</h2>
        </div>
        <div className="genbank-body">
          {loadingGenBank ? (
            <p>Loading GenBank data...</p>
          ) : (
            <>
              
              <button className="copy-button" onClick={() => handleCopy(genBankData[accession])}>
                {copied ? "✅ Copied!" : "📋 Copy GenBank"}
              </button>
              <pre className="genbank-box">{genBankData[accession]}</pre>
            </>
          )}
        </div>
      </div>
    );
  }

  if (activeASN1View) {
    const accession = activeASN1View;
    return (
      <div className="genbank-fullview">
        <div className="genbank-header">
          <button className="back-button" onClick={closeFullASN1View}>← Back to List</button>
          <h2>📄 ASN1 Record: {accession}</h2>
        </div>
        <div className="genbank-body">
          {loadingASN1 ? (
            <p>Loading ASN1 data...</p>
          ) : (
            <>
             
              <button className="copy-button" onClick={() => handleCopy(asn1Data[accession])}>
                {copied ? "✅ Copied!" : "📋 Copy ASN1"}
              </button>
              <pre className="genbank-box">{asn1Data[accession]}</pre>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="nucleotide-page">
      <div className="filter-sidebar">
        <h3>🔎 Filter by</h3>
        <div className="filter-group">
          <label htmlFor="molType">Molecular Type:</label>
          <select id="molType" value={molTypeFilter} onChange={handleMolTypeChange} className="filter-select">
            <option value="all">All</option>
            <option value="DNA">DNA</option>
            <option value="mRNA">mRNA</option>
          </select>
        </div>
      </div>

      <div className="nucleotide-component">
        <h2 className="header1">
          🧬 Nucleotide Data {!loading && totalItems > 0 && <span>({totalItems} results)</span>}
        </h2>

        <Pagination />

        <div className="search-bar" style={{ margin: "1rem 0" }}>
          <input
            placeholder="🔍 Search by accession, title, or organism"
            value={searchQuery}
            onChange={handleSearchChange}
            onKeyDown={handleKeyPress}
          />
        </div>

        {loading && <p>Loading...</p>}
        {!loading && error && <p className="error">{error}</p>}
        {!loading && !error && totalItems === 0 && <p className="error">No results found.</p>}

        {!loading && !error && totalItems > 0 && (
          <div className="nucleotide-list">
            {nucleotides.map((nuc, index) => (
              <div className="nucleotide-card" key={nuc.id}>
                <div className="card-top-row">
                  <span className="nucleotide-title clickable" onClick={() => openFullGenBankView(nuc.accession)}>
                    {(page - 1) * ITEMS_PER_PAGE + index + 1}. {nuc.title || nuc.accession}
                  </span>
                </div>

                <div className="description-block">
                  <p><strong>{nuc.sequence_length}</strong> bp ({nuc.mol_type})</p>
                  <p>
                    <strong>Accession:</strong>{" "}
                    <span className="clickable accession-link" onClick={() => openFullGenBankView(nuc.accession)}>
                      {nuc.accession}
                    </span>
                    {nuc.gi && <> | <strong>GI:</strong> {nuc.gi}</>}
                  </p>
                </div>

                <div className="related-links1">
                  {nuc.tax_id && <a href={`https://www.ncbi.nlm.nih.gov/Taxonomy/Browser/wwwtax.cgi?id=${nuc.tax_id}`} target="_blank" rel="noopener noreferrer" className="link-button">Taxonomy</a>}
                  {nuc.pubmed_id && <a href={`https://pubmed.ncbi.nlm.nih.gov/${nuc.pubmed_id}/`} target="_blank" rel="noopener noreferrer" className="link-button">PubMed</a>}
                  {nuc.biosample_accession && <a href={`https://www.ncbi.nlm.nih.gov/biosample/${nuc.biosample_accession}`} target="_blank" rel="noopener noreferrer" className="link-button">BioSample</a>}
                  <a href={`https://www.ncbi.nlm.nih.gov/nuccore/${nuc.accession}`} target="_blank" rel="noopener noreferrer" className="link-button">NCBI</a>
                </div>

                <div className="fasta-actions">
                  <button className="link-button" onClick={() => toggleFasta(nuc.id)}>FASTA</button>
                  <button className="link-button" onClick={() => openFullGenBankView(nuc.accession)}>GenBank</button>
                  <button className="link-button" onClick={() => openFullASN1View(nuc.accession)}>ASN1</button>
                </div>

                {expandedFasta?.id === nuc.id && (
                  <div className="fasta-box">
              
                    <button className="copy-button" onClick={() => handleCopy(expandedFasta.fasta)}>
                      {copied ? "✅ Copied!" : "📋 Copy FASTA"}
                    </button>
                    <pre>{expandedFasta.fasta}</pre>
                  </div>
                )}

                {expandedGenBank === nuc.accession && (
                  <div className="genbank-box">
                    {loadingGenBank ? <p>Loading GenBank data...</p> : (
                      <>
                        
                        <button className="copy-button" onClick={() => handleCopy(genBankData[nuc.accession])}>
                          {copied ? "✅ Copied!" : "📋 Copy GenBank"}
                        </button>
                        <pre>{genBankData[nuc.accession]}</pre>
                      </>
                    )}
                  </div>
                )}

              </div>
            ))}
          </div>
        )}

        <Pagination />
      </div>
    </div>
  );
};

export default NucleotideComponent;

